import { Target, Eye, Users2 } from "lucide-react";

const AboutPage = () => {
  const team = [
    {
      name: "Tejas Dike",
      role: "Founder & CEO",
      description: "Expert in RERA and real estate regulations with 5+ years experience.",
    },
    {
      name: "CA Saurabh Dukare",
      role: "Associate Partner",
      description: "Chartered Accountant with expertise in GST advisory, ensuring compliance, efficiency, and risk-free operations.",
    },
    {
      name: "CS Ganesh Kalbande",
      role: "Company Secretary",
      description: "Company Secretary specializing in corporate laws, compliance, and governance. Trusted advisor for startups, SMEs, and corporates with a focus on transparency and best practices.",
    },
  ];

  return (
    <div className="min-h-screen fade-in" data-testid="about-page">
      {/* About Section - New Layout */}
      <section className="pt-32 pb-20 md:py-32 section-light" data-testid="about-intro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Text */}
            <div>
              <h1
                className="text-5xl md:text-6xl tracking-tight leading-tight text-[#1A1A1A] mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="about-heading"
              >
                About Us
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-[#555555] mb-4">
                We provide specialized RERA registration, compliance, and advisory services to developers, promoters, and real estate professionals across India. With deep expertise in the Real Estate (Regulation and Development) Act, 2016, we offer precise, well structured guidance to navigate complex regulatory frameworks with clarity and confidence.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-[#555555]">
                Our practice is built on accuracy, discretion, and efficiency ensuring timely approvals, strict compliance, and effective risk management at every stage of a project. From initial registration to ongoing regulatory support, we deliver comprehensive, dependable solutions tailored to the evolving needs of the real estate sector.
              </p>
            </div>

            {/* Right Side - Image */}
            <div className="flex justify-center">
              <div className="relative rounded-lg overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300 group">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/e3ae400f-ac43-4e71-92d0-4fd17486a503/images/74e6f15e36f562aabe39ac9777c26895e9310b92f84d73a18eb993e5262b53d8.png"
                  alt="RERA Hub Team"
                  className="w-full h-auto transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-20 md:py-32 section-grey" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="team-heading"
            >
              Our Team
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#555555] max-w-2xl mx-auto">
              Our team of seasoned professionals brings decades of combined experience in RERA compliance, legal advisory, and business consulting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <div
                key={index}
                className="service-card bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center"
                data-testid={`team-member-${index}`}
              >
                <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                  {index === 0 ? (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_legal-advisory-pro/artifacts/abg43a19_tejas%20dike.jpeg"
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : index === 1 ? (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_legal-advisory-pro/artifacts/5twijzwu_saurabh%20dukare.jpeg"
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : index === 2 ? (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_legal-advisory-pro/artifacts/71x5vk0r_gane%20kalbande%201.jpeg"
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <Users2 className="w-16 h-16 text-[#C9A14A]" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{member.name}</h3>
                <div className="text-sm text-[#C9A14A] font-semibold mb-3">{member.role}</div>
                <p className="text-sm leading-relaxed text-[#555555]">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 section-light" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="service-card bg-white p-10 rounded-lg shadow-sm border border-slate-100" data-testid="mission-card">
              <Target className="w-12 h-12 text-[#C9A14A] mb-6" />
              <h3
                className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Our Mission
              </h3>
              <p className="text-base leading-relaxed text-[#555555]">
                To empower real estate developers and businesses with seamless RERA compliance solutions and strategic advisory services, enabling them to focus on growth while we handle the complexities of legal and regulatory requirements.
              </p>
            </div>

            <div className="service-card bg-white p-10 rounded-lg shadow-sm border border-slate-100" data-testid="vision-card">
              <Eye className="w-12 h-12 text-[#C9A14A] mb-6" />
              <h3
                className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Our Vision
              </h3>
              <p className="text-base leading-relaxed text-[#555555]">
                To be India's most trusted RERA compliance and advisory partner, setting industry standards for excellence, transparency, and client satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 section-grey border-t-4 border-[#C9A14A]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="values-heading"
            >
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center" data-testid="value-integrity">
              <h3 className="text-2xl md:text-3xl font-bold text-[#C9A14A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Integrity
              </h3>
              <p className="text-[#555555] leading-relaxed">
                We uphold the highest standards of honesty and transparency in every client interaction.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center" data-testid="value-excellence">
              <h3 className="text-2xl md:text-3xl font-bold text-[#C9A14A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Excellence
              </h3>
              <p className="text-[#555555] leading-relaxed">
                We strive for perfection in service delivery, ensuring every detail meets our exacting standards.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center" data-testid="value-partnership">
              <h3 className="text-2xl md:text-3xl font-bold text-[#C9A14A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Partnership
              </h3>
              <p className="text-[#555555] leading-relaxed">
                We build long-term relationships, treating your success as our own and growing together.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
